import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api-response';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private readonly baseUrl = environment.apiUrl.replace(/\/+$/, '');

  constructor(private http: HttpClient) {}

  /** Join base (no trailing slash) with path so `api/v1` + `admin/x` does not become `api/v1admin/x`. */
  private url(path: string): string {
    const p = path.replace(/^\/+/, '');
    return `${this.baseUrl}/${p}`;
  }

  private extract<T>(res: ApiResponse<T>): T {
    if (!res.success) throw new Error(res.message || res.errors?.join(', ') || 'Request failed');
    return res.data as T;
  }

  get<T>(endpoint: string, options?: object): Observable<T> {
    return this.http.get<ApiResponse<T>>(this.url(endpoint), options).pipe(
      map((res) => this.extract(res))
    );
  }

  getData<T>(endpoint: string, options?: object): Observable<T> {
    return this.http.get<ApiResponse<T>>(this.url(endpoint), options).pipe(
      map((res) => this.extract(res))
    );
  }

  post<T>(endpoint: string, body: unknown, options?: object): Observable<T> {
    return this.http.post<ApiResponse<T>>(this.url(endpoint), body, options).pipe(
      map((res) => this.extract(res))
    );
  }

  postData<T>(endpoint: string, body: unknown, options?: object): Observable<T> {
    return this.http.post<ApiResponse<T>>(this.url(endpoint), body, options).pipe(
      map((res) => this.extract(res))
    );
  }

  put<T>(endpoint: string, body: unknown, options?: object): Observable<T> {
    return this.http.put<ApiResponse<T>>(this.url(endpoint), body, options).pipe(
      map((res) => this.extract(res))
    );
  }

  putData<T>(endpoint: string, body: unknown, options?: object): Observable<T> {
    return this.http.put<ApiResponse<T>>(this.url(endpoint), body, options).pipe(
      map((res) => this.extract(res))
    );
  }

  patch<T>(endpoint: string, body: unknown, options?: object): Observable<T> {
    return this.http.patch<ApiResponse<T>>(this.url(endpoint), body, options).pipe(
      map((res) => this.extract(res))
    );
  }

  delete<T>(endpoint: string, options?: object): Observable<T> {
    return this.http.delete<ApiResponse<T>>(this.url(endpoint), options).pipe(
      map((res) => this.extract(res))
    );
  }

  deleteData<T = void>(endpoint: string, options?: object): Observable<T> {
    return this.http.delete<ApiResponse<T>>(this.url(endpoint), options).pipe(
      map((res) => this.extract(res))
    );
  }

  /** Upload a file (e.g. to /publishing/upload/cover or /publishing/upload/manuscript). Returns { url }. */
  uploadFile(endpoint: string, file: File): Observable<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<{ url: string }>>(this.url(endpoint), formData).pipe(
      map((res) => this.extract(res))
    );
  }
}
